export type Purchase = {
    id: string;
    [key: string]: any; // Dynamic properties from CSV columns
};

export const parseCSV = (csvContent: string): { purchases: Purchase[], headers: string[] } => {
    const lines = csvContent.split("\n");
    const purchases: Purchase[] = [];

    console.log("Total lines in CSV:", lines.length);

    if (lines.length < 2) {
        console.error("CSV has too few lines");
        return { purchases: [], headers: [] };
    }

    // Detect if first row is a title (has fewer columns or different structure)
    const firstLineValues = parseCSVLine(lines[0]?.trim());
    const thirdLineValues = parseCSVLine(lines[2]?.trim());
    
    // If first line has only 1 value, it's likely a title
    const hasTitle = firstLineValues.length === 1;
    const headerRowIndex = hasTitle ? 1 : 0;
    
    // Check if the row after header contains header-like data (all caps, short values, etc.)
    const isSecondaryHeader = (values: string[]): boolean => {
        // Check if values look like headers (short, mostly caps, or contain parentheses)
        const headerLikeCount = values.filter(v => {
            const val = v.trim();
            return val.length < 30 && (
                val === val.toUpperCase() || 
                val.includes('(') || 
                val.includes('YES/NO') ||
                val.includes('MISSING') ||
                val === ''
            );
        }).length;
        
        return headerLikeCount > values.length / 2;
    };
    
    // Determine data start row - skip secondary headers if present
    let dataStartRow = headerRowIndex + 1;
    if (thirdLineValues.length > 0 && isSecondaryHeader(thirdLineValues)) {
        dataStartRow = headerRowIndex + 2; // Skip both header rows
        console.log("Detected secondary header row, skipping");
    }

    // Parse header
    const headerLine = lines[headerRowIndex]?.trim();
    const originalHeaders = parseCSVLine(headerLine);
    
    // Insert "RISK SCORE" column after "PO NUMBER"
    const poNumberIndex = originalHeaders.findIndex(h => 
        h.toLowerCase().includes('po number') || h.toLowerCase().includes('po_number')
    );
    
    const headers = [...originalHeaders];
    if (poNumberIndex >= 0) {
        headers.splice(poNumberIndex + 1, 0, "RISK SCORE");
        console.log(`Inserted RISK SCORE column after PO NUMBER at index ${poNumberIndex + 1}`);
    } else {
        // If PO NUMBER not found, add it as the 4th column (after common initial columns)
        headers.splice(3, 0, "RISK SCORE");
        console.log("PO NUMBER not found, inserted RISK SCORE at index 3");
    }
    
    console.log("CSV Headers detected:", headers);
    console.log("Number of columns:", headers.length);

    for (let i = dataStartRow; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Split by comma, but respect quoted fields
        const values = parseCSVLine(line);

        if (values.length === 0) {
            console.log(`Row ${i} skipped - empty`);
            continue;
        }

        // Create purchase object with all columns from CSV
        // const purchase: Purchase = {
        //     id: `purchase-${i}`,
        // };

        // // Generate a fake RISK SCORE (1-5)
        // const fakeRiskScore = Math.floor(Math.random() * 5) + 1;

        // // Map all columns to purchase object, including the RISK SCORE
        // let originalColIndex = 0;
        // headers.forEach((header, index) => {
        //     let value: string | number;
            
        //     if (header === "RISK SCORE") {
        //         // Insert fake RISK SCORE
        //         value = fakeRiskScore;
        //     } else {
        //         // Use original CSV value
        //         value = values[originalColIndex] || "";
        //         originalColIndex++;
        //     }
            
        //     // Use header as key, clean up the key name
        //     const key = header.trim().replace(/\s+/g, '_').replace(/[()]/g, '');
        //     purchase[key] = value;
        // });

        // Create purchase object with all columns from CSV
        const purchase: Purchase = {
            id: `purchase-${i}`,
        };

        // Map all columns to purchase object, including the RISK SCORE
        let originalColIndex = 0;
        headers.forEach((header, index) => {
            let value: string | number;

            if (header === "RISK SCORE") {
                // leave blank; will be filled after AI scoring
                value = "";
            } else {
                // Use original CSV value
                value = values[originalColIndex] || "";
                originalColIndex++;
            }

            // Use header as key, clean up the key name
            const key = header.trim().replace(/\s+/g, '_').replace(/[()]/g, '');
            purchase[key] = value;
        });

        purchases.push(purchase);
    }

    console.log(`Successfully parsed ${purchases.length} purchases`);
    console.log("Sample purchase:", purchases[0]);
    return { purchases, headers };
};


// Helper function to parse CSV line respecting quoted fields
const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = "";
        } else {
            current += char;
        }
    }

    result.push(current.trim());
    return result;
};

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

